/* -------------------------------------------------------------------------- */
/*                      STALE BUNDLE (AFTER DEPLOY) RECOVERY                    */
/* -------------------------------------------------------------------------- */

/**
 * A deploy replaces every content-hashed file under /assets/ and deletes the old ones
 * (`aws s3 sync --delete`). A tab, or a cached copy of index.html, that still points at
 * the previous build then asks for a file that is gone, and the SPA fallback answers with
 * index.html (HTTP 200, text/html). The browser refuses that as a module script:
 *   "Failed to load module script: Expected a JavaScript-or-Wasm module script but the
 *    server responded with a MIME type of "text/html""
 * When that file is the ENTRY script, no app code runs at all and the talent sees a blank
 * page until a hard reload, because a plain reload reuses the cached index.html.
 *
 * Recovery = refetch the current document past the HTTP cache (`cache: 'reload'` also
 * overwrites the cached copy) and then reload, so the reload gets the new index.html and
 * with it the new asset names. A short time window (mirrors pro-company-app's
 * config/chunkReload.ts) stops a reload loop when the assets are genuinely broken; the
 * timestamp lives in sessionStorage with a `window.name` fallback, both of which survive a
 * reload in the same tab even when storage is blocked.
 *
 * This file must stay free of imports: it is also inlined into index.html as a classic
 * script (see vite.config.ts), because the entry-script failure happens before any module
 * code can run.
 */

export const RELOAD_WINDOW_MS = 10_000;
export const RELOAD_STORAGE_KEY = 'pta:lastStaleBundleReload';
const WINDOW_NAME_PREFIX = 'pta:lastStaleBundleReload=';

const readLastReload = (win: Window): number | null => {
  try {
    const stored = win.sessionStorage.getItem(RELOAD_STORAGE_KEY);
    if (stored !== null) return Number(stored);
  } catch {
    // sessionStorage unavailable, fall back to window.name below.
  }
  if (win.name.startsWith(WINDOW_NAME_PREFIX)) {
    return Number(win.name.slice(WINDOW_NAME_PREFIX.length));
  }
  return null;
};

const writeLastReload = (win: Window, timestamp: number): void => {
  try {
    win.sessionStorage.setItem(RELOAD_STORAGE_KEY, String(timestamp));
    return;
  } catch {
    // sessionStorage unavailable, fall back to window.name.
  }
  win.name = `${WINDOW_NAME_PREFIX}${timestamp}`;
};

/**
 * Returns true (and records the attempt) when no stale-bundle reload happened in the last
 * RELOAD_WINDOW_MS; false means we already reloaded and it did not help, so the caller must
 * not reload again.
 */
export const claimStaleBundleReload = (win: Window = window): boolean => {
  const last = readLastReload(win);
  const now = Date.now();
  if (last !== null && now - last >= 0 && now - last < RELOAD_WINDOW_MS) {
    return false;
  }
  writeLastReload(win, now);
  return true;
};

/**
 * Refreshes the HTTP-cached copy of the current document, then reloads. The refetch is
 * best effort: if it fails (offline, no fetch) we still reload.
 */
export const reloadWithFreshDocument = (
  win: Window = window
): Promise<void> => {
  const reload = () => win.location.reload();
  if (typeof win.fetch !== 'function') {
    reload();
    return Promise.resolve();
  }
  return win
    .fetch(win.location.href, { cache: 'reload', credentials: 'same-origin' })
    .then(
      () => reload(),
      () => reload()
    );
};

const ASSET_PATH = /\/assets\/[^?#]+\.(?:m?js|css)(?:[?#]|$)/;

const failedAssetUrl = (target: EventTarget | null): string | null => {
  const element = target as {
    tagName?: string;
    src?: string;
    href?: string;
    rel?: string;
  } | null;
  if (!element || typeof element.tagName !== 'string') return null;
  const tag = element.tagName.toUpperCase();
  if (tag === 'SCRIPT' && element.src) return element.src;
  if (
    tag === 'LINK' &&
    element.href &&
    /modulepreload|stylesheet/.test(element.rel ?? '')
  ) {
    return element.href;
  }
  return null;
};

/**
 * Catches load failures of the build's own /assets/ scripts and stylesheets (including the
 * entry script that index.html references) and recovers by reloading with a fresh
 * document, once per RELOAD_WINDOW_MS. Resource `error` events do not bubble, so we listen
 * in the capture phase.
 */
export const installStaleBundleRecovery = (win: Window = window): void => {
  win.addEventListener(
    'error',
    (event: Event) => {
      const url = failedAssetUrl(event.target);
      if (!url || !ASSET_PATH.test(url)) return;
      try {
        if (new URL(url, win.location.href).origin !== win.location.origin) {
          return;
        }
      } catch {
        return;
      }
      if (!claimStaleBundleReload(win)) return;
      void reloadWithFreshDocument(win);
    },
    true
  );
};
